import {
  UserActionTypeAlternateEmailChanged,
  UserActionTypeArchiveOptionsChanged,
  UserActionTypeArea,
  UserActionTypeBackupConfigured,
  UserActionTypeBackupOptionsChanged,
  UserActionTypeCompleted,
  UserActionTypeDeletion,
  UserActionTypeDownloaded,
  UserActionTypeExport,
  UserActionTypePageVisited,
  UserActionTypeRestoreInstalled,
  UserActionTypeRetentionPolicyChanged,
  UserActionTypeTaskPerformed,
  UserActionTypeViewed
} from '@common/enums';
import { DisplayNameDictionary } from '@common/types';

/**
 * `UserActionType` is `action` in model and form
 */
export const UserActionTypeMap: DisplayNameDictionary = {
  [UserActionTypePageVisited.MailVisited]: {
    displayName: 'Mail visited',
    type: 0
  },
  [UserActionTypePageVisited.DriveVisited]: {
    displayName: 'Drive visited',
    type: 1
  },
  [UserActionTypePageVisited.ContactsVisited]: {
    displayName: 'Contacts visited',
    type: 2
  },
  [UserActionTypePageVisited.CalendarVisited]: {
    displayName: 'Calendar visited',
    type: 3
  },
  [UserActionTypePageVisited.SharePointVisited]: {
    displayName: 'SharePoint visited',
    type: 4
  },
  [UserActionTypePageVisited.TeamDrivesVisited]: {
    displayName: 'Shared Drives visited',
    type: 5
  },
  [UserActionTypeRestoreInstalled.MailAllRestoreInitiated]: {
    displayName: 'Initiated restore for all mails',
    type: 6
  },
  [UserActionTypeRestoreInstalled.MailFolderRestoreInitiated]: {
    displayName: 'Initiated restore for mail folder',
    type: 7
  },
  [UserActionTypeRestoreInstalled.MailMessagesRestoreInitiated]: {
    displayName: 'Initiated restore for mail messages',
    type: 8
  },
  [UserActionTypeRestoreInstalled.ContactsAllRestoreInitiated]: {
    displayName: 'Initiated restore for all contacts',
    type: 9
  },
  [UserActionTypeRestoreInstalled.ContactGroupsRestoreInitiated]: {
    displayName: 'Initiated restore for contact groups',
    type: 10
  },
  [UserActionTypeRestoreInstalled.ContactsRestoreInitiated]: {
    displayName: 'Initiated restore for contacts',
    type: 11
  },
  [UserActionTypeRestoreInstalled.CalendarAllRestoreInitiated]: {
    displayName: 'Initiated restore for all calendars',
    type: 12
  },
  [UserActionTypeRestoreInstalled.CalendarGroupsRestoreInitiated]: {
    displayName: 'Initiated restore for calendar groups',
    type: 13
  },
  [UserActionTypeRestoreInstalled.CalendarsRestoreInitiated]: {
    displayName: 'Initiated restore for calendars',
    type: 14
  },
  [UserActionTypeRestoreInstalled.CalendarEventsRestoreInitiated]: {
    displayName: 'Initiated restore for calendar events',
    type: 15
  },
  [UserActionTypeBackupConfigured.UsersConfiguredBackup]: {
    displayName: 'User(s) configured backup',
    type: 16
  },
  [UserActionTypeBackupConfigured.DomainConfiguredBackup]: {
    displayName: 'Domain configured backup',
    type: 17
  },
  [UserActionTypeBackupConfigured.UserTurnedOnOff]: {
    displayName: 'User(s) turned ON/OFF',
    type: 18
  },
  [UserActionTypeBackupConfigured.AllUsersTurnedOn]: {
    displayName: 'All users turned ON',
    type: 19
  },
  [UserActionTypeArea.BackupDeleted]: {
    displayName: 'Backup deleted',
    type: 20
  },
  [UserActionTypeArea.PermissionsSet]: {
    displayName: 'Permissions set',
    type: 21
  },
  [UserActionTypeRetentionPolicyChanged.AddedRetentionPolicy]: {
    displayName: 'Added retention policy',
    type: 22
  },
  [UserActionTypeRetentionPolicyChanged.EditedRetentionPolicy]: {
    displayName: 'Edited retention policy',
    type: 23
  },
  [UserActionTypeRetentionPolicyChanged.DeletedRetentionPolicy]: {
    displayName: 'Deleted retention policy',
    type: 24
  },
  [UserActionTypeRetentionPolicyChanged.AssignedRetentionPolicy]: {
    displayName: 'Assigned retention policy',
    type: 25
  },
  [UserActionTypeAlternateEmailChanged.AddedAlternateEmail]: {
    displayName: 'Added alternate email',
    type: 26
  },
  [UserActionTypeAlternateEmailChanged.EditedAlternateEmail]: {
    displayName: 'Edited alternate email',
    type: 27
  },
  [UserActionTypeAlternateEmailChanged.DeletedAlternateEmail]: {
    displayName: 'Deleted alternate email',
    type: 28
  },
  [UserActionTypeBackupOptionsChanged.AddedBackupOptions]: {
    displayName: 'Added backup options',
    type: 29
  },
  [UserActionTypeBackupOptionsChanged.EditedBackupOptions]: {
    displayName: 'Edited backup options',
    type: 30
  },
  [UserActionTypeBackupOptionsChanged.DeletedBackupOptions]: {
    displayName: 'Deleted backup options',
    type: 31
  },
  [UserActionTypeBackupOptionsChanged.ChangedSpamTrash]: {
    displayName: 'Changed folders',
    type: 32
  },
  [UserActionTypeArchiveOptionsChanged.AddedArchiveOptions]: {
    displayName: 'Added archive options',
    type: 33
  },
  [UserActionTypeArchiveOptionsChanged.EditedArchiveOptions]: {
    displayName: 'Edited archive options',
    type: 34
  },
  [UserActionTypeArchiveOptionsChanged.DeletedArchiveOptions]: {
    displayName: 'Deleted archive options',
    type: 35
  },
  [UserActionTypeTaskPerformed.PausedTask]: {
    displayName: 'Paused task',
    type: 36
  },
  [UserActionTypeTaskPerformed.CanceledTask]: {
    displayName: 'Canceled task',
    type: 37
  },
  [UserActionTypeDownloaded.DriveFileDownloaded]: {
    displayName: 'Drive file downloaded',
    type: 38
  },
  [UserActionTypeDownloaded.SharePointFileDownloaded]: {
    displayName: 'SharePoint file downloaded',
    type: 39
  },
  [UserActionTypeDownloaded.TeamDrivesFileDownloaded]: {
    displayName: 'Shared Drives file downloaded',
    type: 40
  },
  [UserActionTypeDownloaded.MailFileDownloaded]: {
    displayName: 'Mail attachment downloaded',
    type: 41
  },
  [UserActionTypeViewed.MailMessageViewed]: {
    displayName: 'Mail message viewed',
    type: 42
  },
  [UserActionTypeDeletion.MailMessagesDeleted]: {
    displayName: 'Mail messages deleted',
    type: 43
  },
  [UserActionTypeDeletion.MailFoldersDeleted]: {
    displayName: 'Mail folders deleted',
    type: 44
  },
  [UserActionTypeDeletion.ContactGroupsDeleted]: {
    displayName: 'Contact groups deleted',
    type: 45
  },
  [UserActionTypeDeletion.ContactsDeleted]: {
    displayName: 'Contacts deleted',
    type: 46
  },
  [UserActionTypeDeletion.CalendarGroupsDeleted]: {
    displayName: 'Calendar groups deleted',
    type: 47
  },
  [UserActionTypeDeletion.CalendarsDeleted]: {
    displayName: 'Calendars deleted',
    type: 48
  },
  [UserActionTypeDeletion.CalendarEventsDeleted]: {
    displayName: 'Calendar events deleted',
    type: 49
  },
  [UserActionTypeDeletion.DriveItemsDeleted]: {
    displayName: 'Drive items deleted',
    type: 50
  },
  [UserActionTypeDeletion.SharePointItemsDeleted]: {
    displayName: 'SharePoint items deleted',
    type: 51
  },
  [UserActionTypeDeletion.TeamDrivesItemsDeleted]: {
    displayName: 'Shared Drives items deleted',
    type: 52
  },
  [UserActionTypeRestoreInstalled.DriveItemsRestoreInitiated]: {
    displayName: 'Initiated restore for Drive items',
    type: 53
  },
  [UserActionTypeCompleted.MailAllRestoreCompleted]: {
    displayName: 'All mails restore completed',
    type: 54
  },
  [UserActionTypeCompleted.MailFolderRestoreCompleted]: {
    displayName: 'Mail folder restore completed',
    type: 55
  },
  [UserActionTypeCompleted.MailMessagesRestoreCompleted]: {
    displayName: 'Mail messages restore completed',
    type: 56
  },
  [UserActionTypeCompleted.ContactsAllRestoreCompleted]: {
    displayName: 'All contacts restore completed',
    type: 57
  },
  [UserActionTypeCompleted.ContactGroupsRestoreCompleted]: {
    displayName: 'Contact groups restore completed',
    type: 58
  },
  [UserActionTypeCompleted.ContactsRestoreCompleted]: {
    displayName: 'Contacts restore completed',
    type: 59
  },
  [UserActionTypeCompleted.CalendarAllRestoreCompleted]: {
    displayName: 'All calendars restore completed',
    type: 60
  },
  [UserActionTypeCompleted.CalendarGroupsRestoreCompleted]: {
    displayName: 'Calendar groups restore completed',
    type: 61
  },
  [UserActionTypeCompleted.CalendarsRestoreCompleted]: {
    displayName: 'Calendars restore completed',
    type: 62
  },
  [UserActionTypeCompleted.CalendarEventsRestoreCompleted]: {
    displayName: 'Calendar events restore completed',
    type: 63
  },
  [UserActionTypeCompleted.DriveItemsRestoreCompleted]: {
    displayName: 'Drive items restore completed',
    type: 64
  },
  [UserActionTypeExport.ExportInitiated]: {
    displayName: 'Export initiated',
    type: 65
  },
  [UserActionTypeExport.ExportCompleted]: {
    displayName: 'Export completed',
    type: 66
  },
  [UserActionTypeExport.ExportFileDownloaded]: {
    displayName: 'Export file downloaded',
    type: 67
  },
  [UserActionTypeExport.ExportFileDeleted]: {
    displayName: 'Export file deleted',
    type: 68
  },
  [UserActionTypeExport.EditedExportSettings]: {
    displayName: 'Edited export settings',
    type: 69
  },
  [UserActionTypeArea.DefaultRetentionPolicyChanged]: {
    displayName: 'Default retention policy changed',
    type: 70
  },
  [UserActionTypeRestoreInstalled.SharePointItemsRestoreInitiated]: {
    // for Office
    displayName: 'Initiated restore for SharePoint items',
    type: 71
  },
  [UserActionTypeCompleted.SharePointRestoreCompleted]: {
    displayName: 'Completed restore for SharePoint items',
    type: 72
  },
  [UserActionTypeRestoreInstalled.SharedDrivesItemsRestoreInitiated]: {
    // for Google
    displayName: 'Initiated restore for Shared Drives items',
    type: 73
  },
  [UserActionTypeCompleted.SharedDrivesRestoreCompleted]: {
    displayName: 'Completed restore for Shared Drives items',
    type: 74
  },
  [UserActionTypeArea.EditEmailReportSettings]: {
    displayName: 'Edit email report settings',
    type: 75
  },
  [UserActionTypeTaskPerformed.CanceledTasks]: {
    displayName: 'Canceled tasks',
    type: 76
  },
  [UserActionTypeArea.StorageChanged]: {
    displayName: 'Backup storage change',
    type: 77
  },
  [UserActionTypeArea.TeamsItemsRestoreInitiated]: {
    displayName: 'Teams items: restore started',
    type: 78
  },
  [UserActionTypeArea.TeamsItemsRestoreCompleted]: {
    displayName: 'Teams items: restore completed',
    type: 79
  },
  [UserActionTypeArea.SettingsChanged]: {
    displayName: 'Settings changed',
    type: 80
  },
  [UserActionTypeArea.StorageDeleted]: {
    displayName: 'Storage deletion started',
    type: 81
  }
};
